import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MessageCircle, BookOpen, CalendarCheck } from "lucide-react";
import InternalNav from "@/components/InternalNav";
import { REVIEWS as STATIC_REVIEWS, REVIEW_STATS as STATIC_STATS } from "@/content/reviews";
import { useContent } from "@/lib/ContentProvider";


export default function Avis() {
  const { reviews: apiReviews, contact } = useContent();
  const REVIEWS = apiReviews.reviews.length ? apiReviews.reviews : STATIC_REVIEWS;
  const REVIEW_STATS = apiReviews.stats.length ? apiReviews.stats : STATIC_STATS;
  const CONTACT = contact;
  return (
    <div data-testid="avis-page" className="bg-refuge-bg min-h-screen">
      <InternalNav />

      <section className="pt-32 md:pt-40 pb-14 md:pb-20 bg-refuge-bg">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4">Les voyageurs</p>
          <h1 className="font-heading text-4xl md:text-6xl font-light text-refuge-text mb-6 leading-[1.05]">
            Ils ont vécu <span className="italic">le refuge</span>
          </h1>
          <p className="font-heading italic font-light text-refuge-soft/85 text-lg md:text-xl leading-relaxed">
            Des voix sincères, recueillies après leur séjour. Des mots bruts, témoins d'un moment au refuge.
          </p>
        </div>
      </section>

      <section className="pb-16 md:pb-20 bg-refuge-bg">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 py-8 border-t border-b border-refuge-border/60">
            {REVIEW_STATS.map((s) => (
              <div key={s.label} className="text-center"><p className="font-heading text-2xl md:text-3xl text-refuge-ruby mb-2">{s.value}</p><p className="font-body text-[11px] tracking-[0.22em] uppercase text-refuge-soft">{s.label}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28 bg-refuge-bg">
        <div className="max-w-6xl mx-auto px-6">
          <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby text-center mb-4">Témoignages</p>
          <h2 className="font-heading text-3xl md:text-5xl font-light text-refuge-text text-center mb-14 leading-tight">Ce qu'ils <span className="italic">en disent</span></h2>
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 md:gap-8" style={{ columnFill: "balance" }}>
            {REVIEWS.map((r, i) => (
              <article key={i} data-testid={`review-${i}`} className="bg-white p-7 mb-6 md:mb-8 break-inside-avoid rounded-2xl border border-refuge-border/60 transition-all hover:shadow-md">
                <span className="font-heading text-5xl leading-[0.2] block mb-4 text-refuge-rose opacity-70">"</span>
                <p className="font-body text-[15px] leading-[1.7] mb-6 text-refuge-soft whitespace-pre-line">{r.text}</p>
                <div className="pt-4 border-t border-refuge-border/60">
                  <p className="font-heading italic text-lg text-refuge-text">{r.name}</p>
                  <p className="font-body text-[11px] tracking-[0.18em] uppercase text-refuge-soft mt-1">{r.place}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-refuge-cta py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4">À votre tour</p>
          <h2 className="font-heading text-3xl md:text-5xl font-light text-refuge-text mb-10 leading-tight">Écrivez votre propre <span className="italic">séjour</span>.</h2>
          <p className="font-heading italic font-light text-refuge-soft/80 text-lg mb-12">Comment souhaitez-vous préparer votre séjour ?</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/reserver" data-testid="avis-cta-reserver" className="group block bg-white rounded-2xl border border-refuge-border px-7 py-8 h-full transition-all hover:shadow-md hover:scale-[1.02]">
              <CalendarCheck size={20} className="text-refuge-ruby mb-3 mx-auto" strokeWidth={1.1} />
              <p className="font-heading text-lg font-light text-refuge-text mb-2">Choisir mes dates</p>
              <p className="font-body text-sm text-refuge-soft">Voir le calendrier du refuge.</p>
            </Link>
            <a href={`https://wa.me/${CONTACT.whatsappNumber}`} target="_blank" rel="noopener noreferrer" data-testid="avis-cta-whatsapp" className="group block bg-white rounded-2xl border border-refuge-border px-7 py-8 h-full transition-all hover:shadow-md hover:scale-[1.02]">
              <MessageCircle size={20} className="text-refuge-ruby mb-3 mx-auto" strokeWidth={1.1} />
              <p className="font-heading text-lg font-light text-refuge-text mb-2">Envoyer un message</p>
              <p className="font-body text-sm text-refuge-soft">Échanger sur WhatsApp.</p>
            </a>
            <a href={CONTACT.guideUrl} target="_blank" rel="noopener noreferrer" data-testid="avis-cta-guide" className="group block bg-white rounded-2xl border border-refuge-border px-7 py-8 h-full transition-all hover:shadow-md hover:scale-[1.02]">
              <BookOpen size={20} className="text-refuge-ruby mb-3 mx-auto" strokeWidth={1.1} />
              <p className="font-heading text-lg font-light text-refuge-text mb-2">Préparer mon séjour</p>
              <p className="font-body text-sm text-refuge-soft">Le petit guide du refuge.</p>
            </a>
          </div>
        </div>
      </section>

      <div className="bg-refuge-bg py-10 text-center">
        <Link to="/accueil" data-testid="back-to-hub" className="inline-flex items-center gap-2 font-body text-xs tracking-[0.18em] uppercase text-refuge-soft hover:text-refuge-ruby transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.3} /> Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}
